// DIGGII History Modal/Page Component
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Coins, TrendingUp, TrendingDown, Gift, Settings,
  X, Calendar, Filter, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { diggii } from '../lib/api';

const DiggiiHistory = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [historial, setHistorial] = useState([]);
  const [filtro, setFiltro] = useState('todos');

  useEffect(() => {
    if (isOpen) {
      loadHistorial();
    }
  }, [isOpen, filtro]);

  const loadHistorial = async () => {
    setLoading(true);
    try {
      const params = filtro !== 'todos' ? { tipo: filtro } : {};
      const data = await diggii.getHistorial(params);
      setHistorial(data);
    } catch (error) {
      console.error('Error cargando historial:', error);
    }
    setLoading(false);
  };

  const getTipoConfig = (tipo) => {
    const configs = {
      acumulacion: {
        icon: TrendingUp,
        color: 'text-green-500',
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        label: 'Acumulación',
        badge: 'bg-green-100 text-green-700'
      },
      canje: {
        icon: TrendingDown,
        color: 'text-red-500',
        bgColor: 'bg-red-100 dark:bg-red-900/30',
        label: 'Canje',
        badge: 'bg-red-100 text-red-700'
      },
      promocion: {
        icon: Gift,
        color: 'text-purple-500',
        bgColor: 'bg-purple-100 dark:bg-purple-900/30',
        label: 'Promoción',
        badge: 'bg-purple-100 text-purple-700'
      },
      ajuste: {
        icon: Settings,
        color: 'text-blue-500',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30',
        label: 'Ajuste',
        badge: 'bg-blue-100 text-blue-700'
      }
    };
    return configs[tipo] || configs.ajuste;
  };

  const formatFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString('es-GT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5 text-amber-500" />
            Historial DIGGII
          </DialogTitle>
        </DialogHeader>

        {/* Filtros */}
        <div className="flex gap-2 pb-4 border-b overflow-x-auto">
          {[
            { value: 'todos', label: 'Todos' },
            { value: 'acumulacion', label: 'Ganados' },
            { value: 'canje', label: 'Canjeados' },
            { value: 'promocion', label: 'Promociones' }
          ].map((f) => (
            <Button
              key={f.value}
              variant={filtro === f.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFiltro(f.value)}
              className={filtro === f.value ? 'bg-amber-500 hover:bg-amber-600' : ''}
            >
              {f.label}
            </Button>
          ))}
        </div>

        {/* Lista de transacciones */}
        <div className="flex-1 overflow-y-auto space-y-3 py-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
            </div>
          ) : historial.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Coins className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No hay transacciones DIGGII</p>
              <p className="text-sm">¡Realiza pedidos para empezar a acumular!</p>
            </div>
          ) : (
            <AnimatePresence>
              {historial.map((txn, index) => {
                const config = getTipoConfig(txn.tipo);
                const Icon = config.icon;
                const esPositivo = txn.cantidad > 0;

                return (
                  <motion.div
                    key={txn.transaction_id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="border-l-4" style={{ borderLeftColor: esPositivo ? '#22c55e' : '#ef4444' }}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-full ${config.bgColor} flex items-center justify-center flex-shrink-0`}>
                            <Icon className={`h-5 w-5 ${config.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <Badge className={config.badge} variant="secondary">
                                {config.label}
                              </Badge>
                              <span className={`font-bold ${esPositivo ? 'text-green-600' : 'text-red-600'}`}>
                                {esPositivo ? '+' : ''}{txn.cantidad.toFixed(2)} DIGGII
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1 truncate">
                              {txn.descripcion}
                            </p>
                            <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {formatFecha(txn.created_at)}
                              </span>
                              <span>Balance: {txn.balance_nuevo.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DiggiiHistory;
